// ToolbarButton constrols the current editor interaction mode. It
// either displays an icon or a tile.

import { usePluginsContext } from 'app/src/editor/PluginsContext';
import {
	TileInteractionMode,
	useTileInteractionModeContext,
} from 'app/src/editor/TileInteractionModeContext';
import Tile from 'app/src/engine/tiles/Tile';
import * as React from 'react';

export enum ToolbarButtonType {
	AddTile,
	Wall,
	Robot,
	Star,
	Rotate,
	Copy,
}

interface Props {
	type: ToolbarButtonType;
	tile?: Tile;
	rotateClockwise?: boolean;
}

export default function ToolbarButton({ type, tile, rotateClockwise }: Props): React.ReactElement {
	const { mode, setMode, tile: storedTile, setTile } = useTileInteractionModeContext();

	const plugins = usePluginsContext();

	// Figure out if this component is selected or not.
	let selected = false;

	if (
		(type === ToolbarButtonType.Wall && mode === TileInteractionMode.Wall) ||
		(type === ToolbarButtonType.Star && mode === TileInteractionMode.Star) ||
		(type === ToolbarButtonType.Robot && mode === TileInteractionMode.Robot) ||
		(type === ToolbarButtonType.Rotate && mode === TileInteractionMode.Rotate) ||
		(type === ToolbarButtonType.Copy && mode === TileInteractionMode.Copy)
	) {
		selected = true;
	}

	if (type === ToolbarButtonType.AddTile && mode === TileInteractionMode.AddTile) {
		if (storedTile) {
			const storedTileClone = storedTile.clone();
			storedTileClone.walls = new Set();

			if (tile && storedTileClone.isEqualTo(tile)) {
				selected = true;
			}
		}
	}

	// Handlers
	const handleClick = (): void => {
		switch (type) {
			case ToolbarButtonType.AddTile:
				setMode(TileInteractionMode.AddTile);
				if (tile) {
					setTile(tile);
				}
				break;

			case ToolbarButtonType.Wall:
				setMode(TileInteractionMode.Wall);
				break;

			case ToolbarButtonType.Star:
				setMode(TileInteractionMode.Star);
				break;

			case ToolbarButtonType.Robot:
				setMode(TileInteractionMode.Robot);
				break;

			case ToolbarButtonType.Rotate:
				setMode(TileInteractionMode.Rotate);
				break;

			case ToolbarButtonType.Copy:
				setMode(TileInteractionMode.Copy);
				break;
		}
	};

	// Render
	const toolbarButtonClassNames = ['ToolbarButton'];

	if (selected) {
		toolbarButtonClassNames.push('ToolbarButton--selected');
	}

	let child: JSX.Element | null = null;
	let title = '';

	switch (type) {
		case ToolbarButtonType.AddTile: {
			toolbarButtonClassNames.push('ToolbarButton--tile');
			const tilePlugin = tile && Array.from(plugins).find((p) => p.providesNodeForTile(tile));
			child = (tilePlugin && tile && tilePlugin.getTileNode(tile, 0, 0)) || null;
			break;
		}
		case ToolbarButtonType.Wall:
			toolbarButtonClassNames.push('ToolbarButton--icon');
			child = <div className="ToolbarButton-icon ToolbarButton-icon--wall" />;
			title = 'Add Walls (w)';
			break;
		case ToolbarButtonType.Robot:
			toolbarButtonClassNames.push('ToolbarButton--icon');
			child = <div className="ToolbarButton-icon ToolbarButton-icon--robot" />;
			title = 'Add Robot (o)';
			break;
		case ToolbarButtonType.Star:
			toolbarButtonClassNames.push('ToolbarButton--icon');
			child = <div className="ToolbarButton-icon ToolbarButton-icon--star" />;
			title = 'Add Stars (s)';
			break;
		case ToolbarButtonType.Rotate: {
			toolbarButtonClassNames.push('ToolbarButton--icon');
			const clockwise = typeof rotateClockwise === 'boolean' ? rotateClockwise : true;
			child = clockwise ? (
				<div className="ToolbarButton-icon ToolbarButton-icon--rotateClockwise" />
			) : (
				<div className="ToolbarButton-icon ToolbarButton-icon--rotateCounterClockwise" />
			);
			title = 'Rotate Tile (r)';
			break;
		}
		case ToolbarButtonType.Copy:
			toolbarButtonClassNames.push('ToolbarButton--icon');
			child = <div className="ToolbarButton-icon ToolbarButton-icon--copy" />;
			title = 'Copy Tile (c)';
			break;
	}

	return (
		<button
			className={toolbarButtonClassNames.join(' ')}
			onClick={(): void => handleClick()}
			title={title}
		>
			{child}
		</button>
	);
}
